import { useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import axiosInstance from "@/api/config";
import NotificationContent from "@/components/notification/notificationContent";
import { ALL_COLORS, COLORS } from "@/constants/colors";
import { MESSAGE } from "@/constants/message";
import { NPS_MILESTONES } from "@/constants/nps";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { RootState } from "@/store";
import { removeUnOpenedJobId } from "@/store/slice/analysisJob";
import { setIsHasSummary } from "@/store/slice/analysisSummary";
import { addDataset, addRawDataset } from "@/store/slice/datasetSlice";
import { clearCurrRunningAnalysisId } from "@/store/slice/spatialSlice";
import {
  setNpsMilestoneReached,
  setUIDataConfig,
  uiInitialState
} from "@/store/slice/uiSlice";
import {
  AnalysisStatus,
  Dataset,
  DatasetChunkStream,
  DatasetData,
  DatasetDataRowV2,
  DatasetMetadataStream,
  DatasetStreamDone,
  EColorNames
} from "@/types/dataset";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import { computeStepValue } from "@/utils/stepValue";

import { useConfiguration } from "../project/useConfiguration";
import useToast, { EToastType } from "../ui/useToast";
import useEventSource from "../useEventSource";

type AnalysisStream = {
  analysis_status: { id: string; status: AnalysisStatus; message?: string };
  "analysis_result:meta": DatasetMetadataStream[];
  "analysis_result:chunk": DatasetChunkStream;
  "analysis_result:done": DatasetStreamDone;
  "dataset_data:chunk": DatasetChunkStream;
  "dataset_data:done": DatasetStreamDone;
  "stream:done": { dataset_id: string };
  "stream:error": void;
};

export const useAnalysisResult = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const chunkBufferRef = useRef<DatasetDataRowV2[]>([]);
  const datasetMetadataRef = useRef<{
    [id: string]: DatasetMetadataStream;
  }>({});
  const datasetDataRef = useRef<{
    [id: string]: { datasetData: DatasetData; newDataset: Dataset };
  }>({});
  const analysisStatusRef = useRef<AnalysisStatus>();
  const analysisMessageRef = useRef<string>();
  const hasHandledAnalysisErrorRef = useRef(false);

  const currRunningAnalysisId = useSelector(
    (state: RootState) => state.spatial.currRunningAnalysisId
  );

  const { deleteAllFeatures, map } = useMap();

  const {
    handleClearDataset,
    generateDatasetData,
    handleAddSpatialAnalysisDatasetByType
  } = useDataset();
  const { mutateGetAnalysisConfig } = useConfiguration({});

  const handleAnalysisError = () => {
    if (hasHandledAnalysisErrorRef.current) return;

    hasHandledAnalysisErrorRef.current = true;
    dispatch(clearCurrRunningAnalysisId());
    toast.dismiss("spatial-analysis-toast");

    const errorMessage = analysisMessageRef.current || "";
    const [errorTitle, ...errorDescParts] = errorMessage.split("-");
    const errorDescription = errorDescParts.join("-").trim();
    analysisMessageRef.current = undefined;

    showToast({
      type: EToastType.ERROR,
      message: (
        <NotificationContent
          title={errorTitle?.trim() || "Something Went Wrong"}
          description={
            errorDescription ||
            "We’re sorry, we cannot proceed with your request. Please try again."
          }
        />
      )
    });
    analysisStatusRef.current = undefined;
  };

  useEventSource<AnalysisStream>({
    url: `${axiosInstance.defaults.baseURL}/v1/analysis/${currRunningAnalysisId}/stream`,
    eventKey: ["analysis", { id: currRunningAnalysisId }],
    parse: (data) => JSON.parse(data),
    onOpen: () => {
      hasHandledAnalysisErrorRef.current = false;
      showToast({
        id: "spatial-analysis-toast",
        type: EToastType.LOADING,
        message: (
          <NotificationContent
            title={MESSAGE.LOADING_SPATIAL_ANALYSIS}
            description={MESSAGE.PREPARE_TABULAR_DATA_AND_MAPVIEW}
          />
        )
      });
    },
    eventHandlers: {
      analysis_status: (data) => {
        analysisStatusRef.current = data.status;
        if (data.message) {
          analysisMessageRef.current = data.message;
        }

        if (data.status === AnalysisStatus.FAILED) {
          handleAnalysisError();
        }
      },
      "analysis_result:meta": (data) => {
        data.forEach((dataset) => {
          datasetMetadataRef.current[dataset.dataset_id] = dataset;
        });
      },
      "analysis_result:chunk": (data) => {
        chunkBufferRef.current.push(...data.rows);
      },
      "analysis_result:done": (data: DatasetStreamDone) => {
        const dataset = {
          dataset_id: data.dataset_id,
          rows: chunkBufferRef.current,
          seq: data.final_seq
        };

        const { datasetData, newDataset } = generateDatasetData(
          dataset,
          datasetMetadataRef.current[data.dataset_id],
          true
        );

        datasetDataRef.current[data.dataset_id] = {
          datasetData,
          newDataset
        };

        chunkBufferRef.current = [];
      },
      "dataset_data:chunk": (data) => {
        chunkBufferRef.current.push(...data.rows);
      },
      "dataset_data:done": (data) => {
        const dataset = {
          dataset_id: data.dataset_id,
          rows: chunkBufferRef.current,
          seq: data.final_seq
        };

        const { datasetData, newDataset } = generateDatasetData(
          dataset,
          datasetMetadataRef.current[data.dataset_id],
          true
        );

        datasetDataRef.current[data.dataset_id] = {
          datasetData,
          newDataset
        };

        chunkBufferRef.current = [];
      },
      "stream:done": async () => {
        if (analysisStatusRef.current !== AnalysisStatus.SUCCESS) {
          dispatch(clearCurrRunningAnalysisId());
          datasetDataRef.current = {};
          analysisStatusRef.current = undefined;
          analysisMessageRef.current = undefined;

          return;
        }

        if (currRunningAnalysisId) {
          // Clear current running analysis ID first to immediately disable stream re-subscription
          // (prevents new EventSource connection from opening while waiting for slow config fetch)
          dispatch(clearCurrRunningAnalysisId());

          try {
            // Then fetch analysis configuration using the previously active analysis ID
            await mutateGetAnalysisConfig({
              analysisId: currRunningAnalysisId
            });
          } catch (error) {
            console.error(error);
          }
        }

        const finalMessage = analysisMessageRef.current || "";
        const [rawTitle, ...descParts] = finalMessage.split("-");
        const title = rawTitle?.trim() || "";
        const description = descParts.join("-").trim();

        const isHasNoScore = [
          MESSAGE.SPATIAL_ANALYSIS_NO_DATA_AVAILABLE,
          MESSAGE.SPATIAL_ANALYSIS_SCORE_GENERATION_FAILED
        ].includes(title);

        deleteAllFeatures();
        handleClearDataset();
        dispatch(
          setUIDataConfig({
            ...uiInitialState,
            showSpatialAnalysisResultTabularModal: false,
            showComparisonPopup: {
              isShow: false,
              isFromAnalysisResult: false,
              isLimitShow: false
            },
            sideBarMenuTypePopupDisplay: null,
            showCatchmentPopup: false,
            showAnalysisResultPopup: true,
            sidebarUserSettingPopupDisplay: {
              isPanelView: false,
              isPanelAdd: false,
              isPanelEdit: false
            }
          })
        );
        toast.dismiss("spatial-analysis-toast");
        dispatch(removeUnOpenedJobId(currRunningAnalysisId));

        const datasetDataList = [
          ...Object.entries(datasetDataRef.current)
        ].reverse();

        for (const [index, [datasetId, data]] of datasetDataList.entries()) {
          const { datasetData, newDataset } = data;
          const featureCollection =
            convertDatasetResponseToFeatureCollection(datasetData);
          dispatch(addRawDataset({ dataset: datasetData, id: datasetId }));
          dispatch(addDataset({ dataset: newDataset }));

          // Loop through colors if there are more datasets than available colors
          const loopIndex = index % ALL_COLORS.length;
          const colorName = ALL_COLORS[loopIndex] as keyof typeof EColorNames;

          handleAddSpatialAnalysisDatasetByType({
            spatialType: datasetData.spatial_aggregation_type,
            newDataset: {
              ...newDataset,
              style: {
                ...newDataset.style,
                fillColor: COLORS[colorName][500],
                colorFamily: EColorNames[colorName],
                colorSteps: computeStepValue(datasetData.rows.length)
              }
            },
            featureCollection
          });
        }

        dispatch(setIsHasSummary(!isHasNoScore));
        dispatch(
          setNpsMilestoneReached(NPS_MILESTONES.COMPLETED_FIRST_ANALYSIS)
        );

        datasetDataRef.current = {};
        analysisStatusRef.current = undefined;

        analysisMessageRef.current = undefined;

        showToast({
          type: isHasNoScore ? EToastType.WARNING : EToastType.SUCCESS,
          message: (
            <NotificationContent
              title={
                title
                  ? MESSAGE.SPATIAL_ANALYSIS_SCORE_NOT_AVAILABLE_TITLE
                  : MESSAGE.SPATIAL_ANALYSIS_SUCCESS_TITLE
              }
              description={
                description
                  ? MESSAGE.SPATIAL_ANALYSIS_SCORE_NOT_AVAILABLE_DESCRIPTION
                  : MESSAGE.SPATIAL_ANALYSIS_SUCCESS_DESCRIPTION
              }
            />
          )
        });
      },
      "stream:error": handleAnalysisError
    },
    enabled: !!currRunningAnalysisId && map?._loaded
  });
};

export default useAnalysisResult;
